import { useState } from 'react';
import { thumbnailUrl } from '@/api/mediaUrl';
import styles from './Avatar.module.css';

import { useT } from '@/i18n/languageStore';

const PALETTE_SIZE = 8;

/** Same hash as mobile `avatarColorFor`, so a member has the same colour on both clients (index into --avatar-1…8). */
export function avatarColorIndex(seed: string | undefined): number {
  if (!seed) return 0;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) % 4294967291;
  return hash % PALETTE_SIZE;
}

/** "Maya Lopez" → "ML"; one name → its first letter; nothing → "?" (mobile `initialsOf`). */
export function initialsOf(name: string | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1] ?? '') : '';
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

export interface AvatarProps {
  name?: string;
  /** Picks the background colour; usually the member id. Defaults to the name. */
  seed?: string;
  photoUrl?: string | null;
  /** Pixels. Default 44 (the design's list avatar). */
  size?: number;
  /** Decorative when the name is already shown next to it. */
  decorative?: boolean;
  className?: string;
}

/**
 * A member's photo over their initials: the initials show while the photo
 * loads, so an avatar is never blank, and stay if it fails
 * (mobile/src/components/ui/Avatar.tsx). The photo is a server copy sized to
 * the avatar, not the full camera original.
 */
export function Avatar({
  name,
  seed,
  photoUrl,
  size = 44,
  decorative = false,
  className,
}: AvatarProps) {
  const t = useT();
  const [state, setState] = useState<'loading' | 'loaded' | 'failed'>('loading');
  const density = typeof window === 'undefined' ? 2 : Math.max(window.devicePixelRatio || 1, 2);
  const src = photoUrl && state !== 'failed' ? thumbnailUrl(photoUrl, size * density) : undefined;
  const colorIndex = avatarColorIndex(seed ?? name) + 1;

  return (
    <span
      className={[styles.avatar, className].filter(Boolean).join(' ')}
      style={{
        ['--avatar-size' as string]: `${size}px`,
        ['--avatar-bg' as string]: `var(--avatar-${colorIndex})`,
      }}
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': name ?? t("Member") })}
    >
      {state !== 'loaded' ? (
        <span className={styles.initials} aria-hidden="true">
          {t(initialsOf(name))}
        </span>
      ) : null}
      {src ? (
        <img
          className={styles.photo}
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => setState('loaded')}
          onError={() => setState('failed')}
          data-loaded={state === 'loaded' || undefined}
        />
      ) : null}
    </span>
  );
}
